import type { ComponentChildren, JSX } from "preact";
import { cn } from "./cn";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "ghost"
  | "danger"
  | "dangerSolid";
export type ButtonSize = "sm" | "md";

type CommonProps = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  iconLeft?: ComponentChildren;
  iconRight?: ComponentChildren;
  fullWidth?: boolean;
  children?: ComponentChildren;
  class?: string;
};

type AsButton = CommonProps &
  Omit<JSX.IntrinsicElements["button"], "size" | "loading" | "class"> & {
    as?: "button";
  };

type AsAnchor = CommonProps &
  Omit<JSX.IntrinsicElements["a"], "size" | "loading" | "class"> & {
    as: "a";
    href: string;
  };

export type ButtonProps = AsButton | AsAnchor;

const VARIANT: Record<ButtonVariant, string> = {
  primary:
    "bg-accent text-accent-fg hover:bg-accent-hover " +
    "shadow-[var(--shadow-button-primary)]",
  secondary:
    "bg-bg text-fg-muted border border-border hover:border-border-strong hover:bg-bg-subtle shadow-xs",
  ghost: "bg-transparent text-fg-muted hover:bg-bg-subtle hover:text-fg",
  danger:
    "bg-bg text-danger-fg border border-border hover:border-danger-border hover:bg-danger-bg",
  dangerSolid:
    "bg-danger-solid text-white hover:opacity-90 " +
    "shadow-[inset_0_1px_0_0_rgba(255,255,255,0.10),0_1px_2px_rgba(16,24,40,0.12)]",
};

const SIZE: Record<ButtonSize, string> = {
  sm: "h-8 px-3 text-xs gap-1.5 rounded-md",
  md: "h-9 px-4 text-sm gap-1.5 rounded-md",
};

const BASE =
  "inline-flex items-center justify-center whitespace-nowrap font-medium transition " +
  "outline-none focus-visible:ring-2 focus-visible:ring-accent/50 " +
  "focus-visible:ring-offset-1 focus-visible:ring-offset-bg " +
  "disabled:opacity-50 disabled:cursor-not-allowed";

function classFor(p: CommonProps): string {
  return cn(
    BASE,
    VARIANT[p.variant ?? "secondary"],
    SIZE[p.size ?? "md"],
    p.fullWidth && "w-full",
    p.class,
  );
}

function renderInner(p: CommonProps): JSX.Element {
  return (
    <>
      {p.loading ? <Spinner /> : p.iconLeft}
      {p.children}
      {!p.loading && p.iconRight}
    </>
  );
}

export function Button(props: ButtonProps) {
  if (props.as === "a") {
    const {
      as: _as,
      variant: _v,
      size: _s,
      loading: _l,
      iconLeft: _il,
      iconRight: _ir,
      fullWidth: _fw,
      class: _c,
      children: _ch,
      ...anchorProps
    } = props;
    return (
      <a {...anchorProps} class={classFor(props)}>
        {renderInner(props)}
      </a>
    );
  }
  const {
    as: _as,
    variant: _v,
    size: _s,
    loading,
    iconLeft: _il,
    iconRight: _ir,
    fullWidth: _fw,
    class: _c,
    children: _ch,
    disabled,
    // Default to type="button": the HTML default is "submit", which turns any
    // secondary action placed inside a <form> into an accidental submitter.
    // Submit buttons must opt in with an explicit type="submit".
    type = "button",
    ...buttonProps
  } = props;
  return (
    <button
      {...buttonProps}
      type={type}
      disabled={disabled || loading}
      class={classFor(props)}
    >
      {renderInner(props)}
    </button>
  );
}

function Spinner() {
  return (
    <span
      aria-hidden
      class="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-r-transparent"
    />
  );
}
